import { useCallback, useEffect, useState } from "react";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DespachoStatusBadge } from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  COMPLIANCE_STATUS_OPTIONS,
  FILING_TYPE_OPTIONS,
  NOT_APPLICABLE_SUGGESTED_COMMENT,
  complianceStatusLabel,
  complianceStatusVariant,
  filingTypeLabel,
  obligationSourceLabel,
} from "@/modules/despacho/complianceInstanceHelpers";
import InternalReminderDialog, { useEntityAlerts } from "@/pages/despacho/InternalReminderDialog";
import { syncWorkClientFromDespacho } from "@/lib/clientSelection";

export default function ComplianceInstanceDetailDialog({
  instanceId,
  open,
  onOpenChange,
  onUpdated,
  readOnly = false,
}) {
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadState, setLoadState] = useState("idle");
  const [comment, setComment] = useState("");
  const [filingForm, setFilingForm] = useState({
    filing_type: "",
    declared_at: "",
    tax_paid: false,
    client_followup_sent: false,
    client_followup_sent_at: "",
    evidence_comment: "",
  });
  const [busy, setBusy] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const { alerts, lastSent } = useEntityAlerts("instance", instanceId);

  const loadDetail = useCallback(async () => {
    if (!instanceId || !open) return;
    setLoadState("loading");
    try {
      const [detail, hist] = await Promise.all([
        despachoFiscalInfoApi.getComplianceInstance(instanceId),
        despachoFiscalInfoApi.getComplianceInstanceHistory(instanceId),
      ]);
      setItem(detail);
      setHistory(hist);
      syncWorkClientFromDespacho(detail?.fiscal_client_id);
      setComment("");
      setFilingForm({
        filing_type: detail?.filing_type || "",
        declared_at: detail?.declared_at ? String(detail.declared_at).slice(0, 10) : "",
        tax_paid: Boolean(detail?.tax_paid),
        client_followup_sent: Boolean(detail?.client_followup_sent),
        client_followup_sent_at: detail?.client_followup_sent_at
          ? String(detail.client_followup_sent_at).slice(0, 10)
          : "",
        evidence_comment: detail?.evidence_comment || "",
      });
      setLoadState("ready");
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      setItem(null);
      setHistory([]);
      setLoadState("error");
      toast.error(parsed.message);
    }
  }, [instanceId, open]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const patchStatus = async (status, extraComment = "") => {
    if (readOnly || !instanceId) return;
    setBusy(true);
    try {
      const updated = await despachoFiscalInfoApi.patchComplianceInstance(instanceId, {
        status,
        comment: extraComment || comment || undefined,
      });
      setItem(updated);
      toast.success("Estado actualizado.");
      onUpdated?.(updated);
      await loadDetail();
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      toast.error(parsed.message);
    } finally {
      setBusy(false);
    }
  };

  const markNotApplicable = async () => {
    await patchStatus(
      "not_applicable_this_period",
      comment.trim() || NOT_APPLICABLE_SUGGESTED_COMMENT,
    );
  };

  const saveFiling = async ({ confirmFiling = false } = {}) => {
    if (readOnly || !instanceId) return;
    if (!filingForm.filing_type || !filingForm.declared_at) {
      toast.error("Indique tipo de declaración y fecha.");
      return;
    }
    setBusy(true);
    try {
      const updated = await despachoFiscalInfoApi.patchComplianceInstance(instanceId, {
        filing_type: filingForm.filing_type,
        declared_at: filingForm.declared_at,
        tax_paid: filingForm.tax_paid,
        client_followup_sent: filingForm.client_followup_sent,
        client_followup_sent_at: filingForm.client_followup_sent_at || undefined,
        evidence_comment: filingForm.evidence_comment || comment || undefined,
        confirm_filing: confirmFiling,
      });
      setItem(updated);
      toast.success(confirmFiling ? "Declaración confirmada." : "Seguimiento guardado.");
      onUpdated?.(updated);
      await loadDetail();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle de vencimiento</DialogTitle>
          <DialogDescription>
            Instancia fiscal por empresa, obligación y periodo.
          </DialogDescription>
        </DialogHeader>

        {loadState === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando…
          </div>
        ) : null}

        {loadState === "error" ? (
          <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950">
            No se pudo cargar el detalle. Cierre e intente de nuevo desde el listado.
          </div>
        ) : null}

        {!instanceId && open ? (
          <div className="text-sm text-slate-600 py-4">Sin instancia seleccionada.</div>
        ) : null}

        {loadState === "ready" && item ? (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-slate-500 block">Cliente</span>
                <strong>{item.client_name || "—"}</strong>
              </div>
              <div>
                <span className="text-slate-500 block">RFC</span>
                <span>{item.client_rfc || "—"}</span>
              </div>
              <div className="col-span-2">
                <span className="text-slate-500 block">Obligación</span>
                <strong>{item.obligation_name || "—"}</strong>
              </div>
              <div>
                <span className="text-slate-500 block">Autoridad</span>
                <span>{item.authority || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Periodo</span>
                <span>{item.period || item.period_key || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Fecha fiscal</span>
                <span>{item.fecha_fiscal || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Fecha operativa</span>
                <span>{item.fecha_operativa || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Recordatorio</span>
                <span>{item.fecha_recordatorio || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Auxiliar</span>
                <span>{item.assigned_auxiliar_name || "Sin asignar"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Estado</span>
                <DespachoStatusBadge variant={complianceStatusVariant(item.status)}>
                  {item.status_label || complianceStatusLabel(item.status)}
                </DespachoStatusBadge>
              </div>
              <div>
                <span className="text-slate-500 block">Fuente obligación</span>
                <span>{obligationSourceLabel(item.obligation_source)}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Regla usada</span>
                <span>{item.rule_source_label || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Declaración</span>
                <span>{item.filing_type_label || filingTypeLabel(item.filing_type)}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Impuesto</span>
                <span>{item.tax_payment_label || (item.tax_paid ? "Pagado" : "Pendiente")}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Recordatorio cliente</span>
                <span>{item.client_reminder_label || "—"}</span>
              </div>
            </div>

            {!readOnly ? (
              <div className="space-y-3 border-t pt-3" data-testid="compliance-filing-form">
                <p className="text-xs font-semibold text-[#0F2D5C] m-0">Seguimiento de declaración</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo</Label>
                    <Select
                      value={filingForm.filing_type || undefined}
                      onValueChange={(v) => setFilingForm((f) => ({ ...f, filing_type: v }))}
                    >
                      <SelectTrigger className="rounded-sm h-8 text-sm">
                        <SelectValue placeholder="Seleccione" />
                      </SelectTrigger>
                      <SelectContent>
                        {FILING_TYPE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="declared-at" className="text-xs">Fecha declaración</Label>
                    <Input
                      id="declared-at"
                      type="date"
                      className="rounded-sm h-8 text-sm"
                      value={filingForm.declared_at}
                      onChange={(e) => setFilingForm((f) => ({ ...f, declared_at: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={filingForm.tax_paid}
                      onCheckedChange={(v) => setFilingForm((f) => ({ ...f, tax_paid: Boolean(v) }))}
                    />
                    El cliente ya pagó el impuesto
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={filingForm.client_followup_sent}
                      onCheckedChange={(v) => setFilingForm((f) => ({ ...f, client_followup_sent: Boolean(v) }))}
                    />
                    Seguimiento enviado al cliente
                  </label>
                </div>
                {filingForm.client_followup_sent ? (
                  <div className="space-y-1">
                    <Label htmlFor="followup-sent-at" className="text-xs">Fecha seguimiento enviado</Label>
                    <Input
                      id="followup-sent-at"
                      type="date"
                      className="rounded-sm h-8 text-sm"
                      value={filingForm.client_followup_sent_at}
                      onChange={(e) => setFilingForm((f) => ({ ...f, client_followup_sent_at: e.target.value }))}
                    />
                  </div>
                ) : null}
                <SpellcheckTextarea
                  id="filing-evidence"
                  label="Comentario / evidencia"
                  value={filingForm.evidence_comment}
                  onChange={(e) => setFilingForm((f) => ({ ...f, evidence_comment: e.target.value }))}
                  rows={2}
                  placeholder="Acuse, observaciones, motivo complementaria…"
                />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" disabled={busy} onClick={() => saveFiling({ confirmFiling: false })}>
                    Guardar seguimiento
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    className="bg-[#0F2D5C] hover:bg-[#0a2248]"
                    disabled={busy}
                    onClick={() => saveFiling({ confirmFiling: true })}
                  >
                    Confirmar y no recordar más
                  </Button>
                </div>
                {!filingForm.tax_paid ? (
                  <p className="text-xs text-amber-800 m-0">
                    Si no marca impuesto pagado, quedará en «Complementarias por pagar» hasta confirmar el pago.
                  </p>
                ) : null}
              </div>
            ) : null}

            {!readOnly ? (
              <div className="border-t pt-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {alerts.some((a) => ["pending", "sent"].includes(a.status)) ? (
                    <span className="text-xs text-amber-700 flex items-center gap-1">
                      <Bell className="h-3 w-3" aria-hidden />
                      Tiene recordatorio activo
                    </span>
                  ) : null}
                  {lastSent?.sent_at ? (
                    <span className="text-xs text-slate-500">
                      Último recordatorio: {String(lastSent.sent_at).slice(0, 16)}
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-7"
                    onClick={() => setReminderOpen(true)}
                  >
                    Enviar recordatorio interno
                  </Button>
                </div>
              </div>
            ) : null}

            {!readOnly ? (
              <div className="space-y-2 border-t pt-3">
                <SpellcheckTextarea
                  id="compliance-comment"
                  label="Comentario"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  placeholder="Comentario opcional al cambiar estado…"
                />
                <div className="flex flex-wrap gap-2 pt-2">
                  {COMPLIANCE_STATUS_OPTIONS.filter((o) => o.value !== "not_applicable_this_period").map((opt) => (
                    <Button
                      key={opt.value}
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => patchStatus(opt.value)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={markNotApplicable}
                  >
                    No aplicó este mes
                  </Button>
                </div>
              </div>
            ) : null}

            {history.length ? (
              <div className="border-t pt-3">
                <p className="font-medium mb-2">Historial</p>
                <ul className="space-y-1 text-xs text-slate-600 max-h-32 overflow-y-auto">
                  {history.map((h) => (
                    <li key={h.id}>
                      {h.created_at?.slice(0, 16)} — {h.action}
                      {h.comment ? `: ${h.comment}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
      <InternalReminderDialog
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        context={
          item
            ? {
                fiscalClientId: item.fiscal_client_id,
                complianceInstanceId: item.id,
                recipientUserId: item.assigned_auxiliar_id,
                recipientLabel: item.assigned_auxiliar_name || "Auxiliar asignado",
                defaultMessage: `Recordatorio: ${item.obligation_name || "Obligación"} de ${item.client_name || "cliente"} correspondiente a ${item.period || "periodo"} vence el ${item.fecha_fiscal || "—"}.`,
              }
            : null
        }
        onSent={loadDetail}
      />
    </Dialog>
  );
}
